import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { ChartLegend, chartColor, createValueFormatter, defaultChartColor, } from '../../../internal/charts/index.js';
import { cx } from '../../../internal/cx.js';
import { formatShare } from '../../../internal/format-number.js';
export { formatShare } from '../../../internal/format-number.js';
const TONE_COLOR = {
    success: 'var(--mn-color-success-solid)',
    warning: 'var(--mn-color-warning-solid)',
    danger: 'var(--mn-color-danger-solid)',
    info: 'var(--mn-color-info-solid)',
    neutral: 'var(--mn-color-text-subtle)',
};
function isDev() {
    const env = globalThis.process
        ?.env;
    return env?.NODE_ENV !== 'production';
}
/**
 * One horizontal bar split into proportions (pipeline stages, storage by
 * type, monitors by state) with a legend of values and shares. The bar is
 * decorative; the legend is a real list, so the data never depends on colour.
 */
export const StackedProportionBar = forwardRef(function StackedProportionBar({ segments, label, total: totalProp, remainderLabel, legendContent = 'both', legend = true, hideZero = true, minSegmentWidth, size = 'md', locale, valueFormat, formatValue, percentDigits = 0, emptyLabel = 'No data yet', className, style, ...rest }, ref) {
    const fmt = createValueFormatter(locale, valueFormat, formatValue);
    const list = segments;
    const toned = list.filter((s) => s.tone !== undefined).length;
    const statusMode = toned > 0 && toned === list.length;
    if (toned > 0 && !statusMode && isDev()) {
        console.warn(`StackedProportionBar "${label}": mix of status tones and palette colours. Give every segment a tone (status mode) or none; tones are ignored.`);
    }
    const parts = list.map((s, i) => ({
        ...s,
        value: Number.isFinite(s.value) && s.value > 0 ? s.value : 0,
        fill: statusMode && s.tone ? TONE_COLOR[s.tone] : chartColor(s.color ?? defaultChartColor(i)),
        color: s.color ?? defaultChartColor(i),
    }));
    const sum = parts.reduce((t, s) => t + s.value, 0);
    const total = Math.max(sum, totalProp ?? sum);
    const remainder = total - sum;
    const shown = hideZero ? parts.filter((s) => s.value > 0) : parts;
    const share = (v) => formatShare(v, total, {
        ...(locale !== undefined && { locale }),
        maximumFractionDigits: percentDigits,
    });
    const describe = (name, v) => {
        if (legendContent === 'none')
            return name;
        if (legendContent === 'value')
            return `${name} ${fmt(v)}`;
        if (legendContent === 'percent')
            return `${name} ${share(v)}`;
        return `${name} ${fmt(v)} · ${share(v)}`;
    };
    const items = shown.map((s) => ({ key: s.key, label: describe(s.label, s.value), color: s.color, fill: s.fill }));
    if (remainder > 0 && remainderLabel) {
        items.push({
            key: '__remainder',
            label: describe(remainderLabel, remainder),
            color: 'neutral',
            fill: 'var(--mn-color-bg-muted)',
        });
    }
    const vars = {};
    if (minSegmentWidth !== undefined)
        vars['--mn-proportion-min'] = minSegmentWidth;
    return (_jsxs("div", { ref: ref, ...rest, className: cx('mn-stacked-proportion-bar', className), "data-size": size, "data-mode": statusMode ? 'status' : undefined, "data-empty": total > 0 ? undefined : '', "data-remainder": remainder > 0 && remainderLabel ? '' : undefined, style: { ...vars, ...style }, children: [_jsxs("div", { className: "mn-stacked-proportion-bar__track", "aria-hidden": "true", children: [shown
                        .filter((s) => s.value > 0)
                        .map((s) => (_jsx("span", { className: "mn-stacked-proportion-bar__segment", "data-tone": statusMode ? s.tone : undefined, style: { ['--_c']: s.fill, flexGrow: s.value }, title: `${s.label}: ${fmt(s.value)} (${share(s.value)})` }, s.key))), remainder > 0 ? (_jsx("span", { className: "mn-stacked-proportion-bar__remainder", style: { flexGrow: remainder }, title: remainderLabel ? `${remainderLabel}: ${fmt(remainder)}` : undefined })) : null] }), items.length > 0 && statusMode ? (_jsx("ul", { className: cx('mn-chart__legend mn-stacked-proportion-bar__legend', legend ? undefined : 'mn-visually-hidden'), "aria-label": label, children: items.map((item) => (_jsxs("li", { className: "mn-chart__legend-item", children: [_jsx("span", { className: "mn-chart__swatch", "data-marker": "square", "aria-hidden": "true", style: { ['--_c']: item.fill } }), _jsx("span", { className: "mn-chart__legend-label", children: item.label })] }, item.key))) })) : items.length > 0 ? (_jsx(ChartLegend, { items: items, marker: "square", label: label, className: cx('mn-stacked-proportion-bar__legend', legend ? undefined : 'mn-visually-hidden') })) : (_jsxs("p", { className: "mn-stacked-proportion-bar__empty", children: [_jsxs("span", { className: "mn-visually-hidden", children: [label, ": "] }), emptyLabel] }))] }));
});
//# sourceMappingURL=stacked-proportion-bar.js.map